import { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'
import { useAuth } from '../hooks/useAuth'
import {Mail, Send, Calendar, Users, Plus, Edit, Trash2, TestTube, TrendingUp, BarChart3} from 'lucide-react'
import toast, { Toaster } from 'react-hot-toast'

interface Campaign {
  id: string
  title: string
  subject: string
  htmlContent: string
  status: 'draft' | 'scheduled' | 'sent' | 'failed'
  scheduledAt?: string
  sentAt?: string
  recipientCount?: number
  createdAt: string
  abTestEnabled?: boolean
  subjectVariantB?: string
  splitPercentage?: number
  winnerVariant?: 'A' | 'B' | 'none'
  variantAOpens?: number
  variantBOpens?: number
  variantAClicks?: number
  variantBClicks?: number
  variantASent?: number
  variantBSent?: number
}

interface Subscriber {
  id: string
  email: string
  subscribedAt: string
  status: 'active' | 'unsubscribed'
}

export default function AdminNewsletter() {
  const { user } = useAuth()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [subscribers, setSubscribers] = useState<Subscriber[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    htmlContent: '',
    scheduledAt: '',
    abTestEnabled: false,
    subjectVariantB: '',
    splitPercentage: 50
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [campaignsRes, subscribersRes] = await Promise.all([
        lumi.entities.newsletterCampaigns.list({ sort: { createdAt: -1 } }),
        lumi.entities.newsletterSubscribers.list({ 
          filter: { status: 'active' },
          sort: { subscribedAt: -1 } 
        })
      ])
      setCampaigns(campaignsRes.list)
      setSubscribers(subscribersRes.list)
    } catch (error) {
      console.error('Load error:', error)
      toast.error('Failed to load data')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    if (formData.abTestEnabled && !formData.subjectVariantB) {
      toast.error('Please provide Variant B subject line for A/B testing')
      return
    }

    setLoading(true)
    try {
      const campaignData: any = {
        title: formData.title,
        subject: formData.subject,
        htmlContent: formData.htmlContent,
        status: formData.scheduledAt ? 'scheduled' : 'draft',
        createdBy: user.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        recipientCount: subscribers.length,
        abTestEnabled: formData.abTestEnabled,
        splitPercentage: formData.splitPercentage,
        winnerVariant: 'none'
      }

      if (formData.scheduledAt) {
        campaignData.scheduledAt = formData.scheduledAt
      }

      if (formData.abTestEnabled) {
        campaignData.subjectVariantB = formData.subjectVariantB
        campaignData.variantAOpens = 0
        campaignData.variantBOpens = 0
        campaignData.variantAClicks = 0
        campaignData.variantBClicks = 0
        campaignData.variantASent = 0
        campaignData.variantBSent = 0
      }

      if (editingId) {
        await lumi.entities.newsletterCampaigns.update(editingId, campaignData)
        toast.success('Campaign updated successfully')
      } else {
        await lumi.entities.newsletterCampaigns.create(campaignData)
        toast.success('Campaign created successfully')
      }

      resetForm()
      loadData()
    } catch (error) {
      console.error('Save error:', error)
      toast.error('Failed to save campaign')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (campaign: Campaign) => {
    setEditingId(campaign.id)
    setFormData({
      title: campaign.title,
      subject: campaign.subject,
      htmlContent: campaign.htmlContent,
      scheduledAt: campaign.scheduledAt || '',
      abTestEnabled: campaign.abTestEnabled || false,
      subjectVariantB: campaign.subjectVariantB || '',
      splitPercentage: campaign.splitPercentage || 50
    })
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return
    
    try {
      await lumi.entities.newsletterCampaigns.delete(id)
      toast.success('Campaign deleted')
      loadData()
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('Failed to delete campaign')
    }
  }

  const handleSendNow = async (campaign: Campaign) => {
    if (!confirm(`Send "${campaign.title}" to ${subscribers.length} subscribers now?`)) return

    setLoading(true)
    try {
      const updateData: any = {
        status: 'sent',
        sentAt: new Date().toISOString(),
        recipientCount: subscribers.length,
        updatedAt: new Date().toISOString()
      }

      if (campaign.abTestEnabled) {
        const splitIndex = Math.floor(subscribers.length * (campaign.splitPercentage || 50) / 100)
        updateData.variantASent = splitIndex
        updateData.variantBSent = subscribers.length - splitIndex
      }

      await lumi.entities.newsletterCampaigns.update(campaign.id, updateData)

      toast.success(`Campaign sent to ${subscribers.length} subscribers!`)
      loadData()
    } catch (error) {
      console.error('Send error:', error)
      toast.error('Failed to send campaign')
    } finally {
      setLoading(false)
    }
  }

  const handleSendTest = async () => {
    if (!formData.subject || !formData.htmlContent) {
      toast.error('Subject and content are required')
      return
    }

    setLoading(true)
    try {
      const { list: testSubscribers } = await lumi.entities.newsletterSubscribers.list({
        filter: { status: 'active' },
        limit: 3
      })

      if (testSubscribers.length === 0) {
        toast.error('No subscribers available for testing')
        return
      }

      const testEmails = testSubscribers.map(s => s.email)

      await lumi.tools.email.send({
        to: testEmails,
        subject: `[TEST] ${formData.subject}`,
        fromName: 'GlenCode Deals',
        html: formData.htmlContent,
        replyTo: 'support@glencode.com'
      })

      if (formData.abTestEnabled && formData.subjectVariantB) {
        await lumi.tools.email.send({
          to: testEmails,
          subject: `[TEST VARIANT B] ${formData.subjectVariantB}`,
          fromName: 'GlenCode Deals',
          html: formData.htmlContent,
          replyTo: 'support@glencode.com'
        })
      }

      toast.success(`Test email(s) sent to ${testEmails.length} subscriber(s)`)
    } catch (error: any) {
      console.error('Test send error:', error)
      toast.error(error?.data?.error || 'Failed to send test email')
    } finally {
      setLoading(false)
    }
  }

  const calculateABResults = (campaign: Campaign) => {
    if (!campaign.abTestEnabled) return null

    const variantASent = campaign.variantASent || 0
    const variantBSent = campaign.variantBSent || 0
    const variantAOpens = campaign.variantAOpens || 0
    const variantBOpens = campaign.variantBOpens || 0
    const variantAClicks = campaign.variantAClicks || 0
    const variantBClicks = campaign.variantBClicks || 0

    const variantAOpenRate = variantASent > 0 ? (variantAOpens / variantASent * 100) : 0
    const variantBOpenRate = variantBSent > 0 ? (variantBOpens / variantBSent * 100) : 0
    const variantACTR = variantAOpens > 0 ? (variantAClicks / variantAOpens * 100) : 0
    const variantBCTR = variantBOpens > 0 ? (variantBClicks / variantBOpens * 100) : 0

    return {
      variantA: {
        sent: variantASent,
        opens: variantAOpens,
        clicks: variantAClicks,
        openRate: variantAOpenRate.toFixed(1),
        ctr: variantACTR.toFixed(1)
      },
      variantB: {
        sent: variantBSent,
        opens: variantBOpens,
        clicks: variantBClicks,
        openRate: variantBOpenRate.toFixed(1),
        ctr: variantBCTR.toFixed(1)
      },
      winner: variantAOpenRate > variantBOpenRate ? 'A' : variantBOpenRate > variantAOpenRate ? 'B' : 'tie'
    }
  }

  const resetForm = () => {
    setFormData({ 
      title: '', 
      subject: '', 
      htmlContent: '', 
      scheduledAt: '',
      abTestEnabled: false,
      subjectVariantB: '',
      splitPercentage: 50
    })
    setEditingId(null)
    setShowForm(false)
  }

  const getStatusBadge = (status: string) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      scheduled: 'bg-blue-100 text-blue-700',
      sent: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700'
    }
    return colors[status as keyof typeof colors] || colors.draft
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600">Please sign in to access newsletter management</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <Toaster position="top-center" />
      
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Newsletter Management</h1>
        <p className="text-gray-600">Create and send campaigns with A/B testing to your subscribers</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-md border-2 border-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Active Subscribers</p>
              <p className="text-3xl font-bold text-blue-600">{subscribers.length}</p>
            </div>
            <Users className="w-12 h-12 text-blue-600 opacity-20" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-md border-2 border-green-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Campaigns</p>
              <p className="text-3xl font-bold text-green-600">{campaigns.length}</p>
            </div>
            <Mail className="w-12 h-12 text-green-600 opacity-20" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-md border-2 border-purple-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Sent Campaigns</p>
              <p className="text-3xl font-bold text-purple-600">
                {campaigns.filter(c => c.status === 'sent').length}
              </p>
            </div>
            <Send className="w-12 h-12 text-purple-600 opacity-20" />
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-md border-2 border-orange-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">A/B Tests Running</p>
              <p className="text-3xl font-bold text-orange-600">
                {campaigns.filter(c => c.abTestEnabled && c.status === 'sent').length}
              </p>
            </div>
            <BarChart3 className="w-12 h-12 text-orange-600 opacity-20" />
          </div>
        </div>
      </div>

      {/* Campaign Form */}
      {showForm && (
        <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {editingId ? 'Edit Campaign' : 'Create New Campaign'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Campaign Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                placeholder="Weekly Deals Newsletter"
              />
            </div>

            {/* A/B Testing Toggle */}
            <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-lg">
              <input
                type="checkbox"
                id="abTestEnabled"
                checked={formData.abTestEnabled}
                onChange={(e) => setFormData({ ...formData, abTestEnabled: e.target.checked })}
                className="w-5 h-5 text-orange-600 rounded focus:ring-2 focus:ring-orange-500"
              />
              <label htmlFor="abTestEnabled" className="flex items-center gap-2 text-sm font-semibold text-gray-800 cursor-pointer">
                <TrendingUp className="w-5 h-5 text-orange-600" />
                Enable A/B Testing for Subject Lines
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {formData.abTestEnabled ? 'Subject Line - Variant A' : 'Email Subject'}
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  placeholder="🔥 New Glen Ivy Coupons This Week!"
                />
              </div>

              {formData.abTestEnabled && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Subject Line - Variant B
                  </label>
                  <input
                    type="text"
                    value={formData.subjectVariantB}
                    onChange={(e) => setFormData({ ...formData, subjectVariantB: e.target.value })}
                    required={formData.abTestEnabled}
                    className="w-full px-4 py-3 border-2 border-orange-300 rounded-lg focus:border-orange-500 focus:outline-none"
                    placeholder="💰 Exclusive Deals Inside!"
                  />
                </div>
              )}
            </div>

            {formData.abTestEnabled && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Traffic Split: {formData.splitPercentage}% Variant A / {100 - formData.splitPercentage}% Variant B
                </label>
                <input
                  type="range"
                  min="30"
                  max="70"
                  value={formData.splitPercentage}
                  onChange={(e) => setFormData({ ...formData, splitPercentage: parseInt(e.target.value) })}
                  className="w-full h-3 bg-gradient-to-r from-blue-200 to-orange-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>30%</span>
                  <span>50%</span>
                  <span>70%</span>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                HTML Content
              </label>
              <textarea
                value={formData.htmlContent}
                onChange={(e) => setFormData({ ...formData, htmlContent: e.target.value })}
                required
                rows={10}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none font-mono text-sm"
                placeholder="<h1>Hello!</h1><p>Check out these amazing deals...</p>"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Schedule Send (Optional)
              </label>
              <input
                type="datetime-local"
                value={formData.scheduledAt}
                onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty to save as draft</p>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50"
              >
                {loading ? 'Saving...' : editingId ? 'Update Campaign' : 'Create Campaign'}
              </button>
              <button
                type="button"
                onClick={handleSendTest}
                disabled={loading || !formData.subject || !formData.htmlContent}
                className="flex items-center gap-2 px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-all disabled:opacity-50"
              >
                <TestTube className="w-4 h-4" />
                Send Test
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Create Button */}
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="mb-6 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-lg hover:from-green-700 hover:to-blue-700 transition-all shadow-md"
        >
          <Plus className="w-5 h-5" />
          Create New Campaign
        </button>
      )}

      {/* Campaigns List */}
      <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200">
        <div className="p-6 border-b-2 border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">All Campaigns</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Title</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Subject</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">A/B Test</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Recipients</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Date</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {campaigns.map((campaign) => {
                const abResults = calculateABResults(campaign)
                return (
                  <tr key={campaign.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{campaign.title}</div>
                      {campaign.abTestEnabled && abResults && campaign.status === 'sent' && (
                        <div className="text-xs mt-2 space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-blue-600">A:</span>
                            <span className="text-gray-600">{abResults.variantA.openRate}% open</span>
                            <span className="text-gray-400">|</span>
                            <span className="text-gray-600">{abResults.variantA.ctr}% CTR</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-orange-600">B:</span>
                            <span className="text-gray-600">{abResults.variantB.openRate}% open</span>
                            <span className="text-gray-400">|</span>
                            <span className="text-gray-600">{abResults.variantB.ctr}% CTR</span>
                          </div>
                          {abResults.winner !== 'tie' && (
                            <div className="flex items-center gap-1 text-green-600 font-semibold">
                              <TrendingUp className="w-3 h-3" />
                              Winner: Variant {abResults.winner}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-gray-900">{campaign.subject}</div>
                      {campaign.abTestEnabled && campaign.subjectVariantB && (
                        <div className="text-xs text-orange-600 mt-1">
                          B: {campaign.subjectVariantB}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(campaign.status)}`}>
                        {campaign.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {campaign.abTestEnabled ? (
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">
                          A/B Test
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-600">{campaign.recipientCount || 0}</td>
                    <td className="px-6 py-4 text-gray-600 text-sm">
                      {new Date(campaign.sentAt || campaign.scheduledAt || campaign.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {campaign.status === 'draft' && (
                          <>
                            <button
                              onClick={() => handleEdit(campaign)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleSendNow(campaign)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Send Now"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {campaign.status !== 'sent' && (
                          <button
                            onClick={() => handleDelete(campaign.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {campaigns.length === 0 && (
            <div className="text-center py-12">
              <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No campaigns yet. Create your first one!</p>
            </div>
          )}
        </div>
      </div>

      {/* Subscribers List */}
      <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 mt-8">
        <div className="p-6 border-b-2 border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Active Subscribers</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Subscribed Date</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {subscribers.map((sub) => (
                <tr key={sub.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">{sub.email}</td>
                  <td className="px-6 py-4 text-gray-600">
                    {new Date(sub.subscribedAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                      {sub.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {subscribers.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No subscribers yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}